import React from 'react'

import { QA } from '@hozana/tests/constants'
import { EVENT_ORIGIN } from '@hozana/tracking/constants'

import { Cell } from 'elements/layout/Cell'
import { PreloadDiv } from 'elements/layout/PreloadDiv'
import { Row, TRowProps } from 'elements/layout/Row'
import { StretchCell } from 'elements/layout/StretchCell'
import { EllipsisMultiLines } from 'elements/text/EllipsisMultiLines'
import { Link, TLinkProps } from 'elements/text/Link'

import { PAGE } from 'routes/constants'

import { CommunityMembers } from 'modules/community/components/CommunityMembers'
import { COMMUNITY_AVATAR_SIZES } from 'modules/community/constants'
import { isCommunityPublished } from 'modules/community/functions'
import { useCommunity } from 'modules/community/hooks/useCommunity'

import { CommunityAvatar, TCommunityAvatarProps } from './CommunityAvatar'

export type TCommunityThumbnailProps = {
  eventOrigin?: EVENT_ORIGIN
  communityId: number
} & Omit<TRowProps, 'onClick'> &
  Pick<TLinkProps, 'onClick'> &
  Pick<TCommunityAvatarProps, 'size'>

export const CommunityThumbnail: TFCC<TCommunityThumbnailProps> = ({
  communityId,
  size = COMMUNITY_AVATAR_SIZES.MEDIUM,
  eventOrigin,
  onClick,
  ...otherProps
}) => {
  const community = useCommunity({ communityId })

  const to = {
    pathname: isCommunityPublished(community) ? PAGE.COMMUNITY_VIEW : PAGE.COMMUNITY_VIEW_ADMIN,
    query: {
      communityId: community?.id,
      communitySlug: community?.slug,
      origin: eventOrigin,
    },
  }

  return (
    <Row data-testid={QA.COMMUNITY_COMMON.COMMUNITY_THUMBNAIL} gutter="1em" {...otherProps} align="left">
      <Cell w="85px">
        {community ? (
          <Link to={to} reload locale={community.lang} onClick={onClick}>
            <CommunityAvatar src={community.picture} size={size} />
          </Link>
        ) : (
          <PreloadDiv h="72px" w="70px" radius="big" />
        )}
      </Cell>
      <StretchCell m="0 5px 0 0">
        {community ? (
          <>
            <Link to={to} onClick={onClick}>
              <EllipsisMultiLines fontSize="1em" bold align="left" lines="2">
                {community.name}
              </EllipsisMultiLines>
            </Link>
            <CommunityMembers community={community} fontSize="1em" lineHeight="1.7em" />
          </>
        ) : (
          <>
            <PreloadDiv h="24px" w="225px" m="5px" radius="small" />
            <PreloadDiv h="24px" w="50px" m="5px" radius="small" />
          </>
        )}
      </StretchCell>
    </Row>
  )
}
